import { itemFromtask } from '@features/task-picker/helpers'
import { useDebounce } from '@hooks/useDebounce'
import { useSearchTasks } from '@hooks/useSearchTasks'
import { FC, ReactNode } from 'react'
import { Item, SkeletonItem } from '../../../components/Item'
import { Column } from '../Column'
import { ActiveFilter } from '../types'
import { getActiveFromFilter } from '../utils/getActiveFromFilter'
import { getItemsAndTotalCount } from '../utils/getItemsAndTotalCount'

interface Props {
  children?: ReactNode
  disabledTaskIds: Set<number>
  displaySearch?: boolean
  filter?: ActiveFilter
  heading?: string
  integrationId: number
  onActiveFilterChange: (filter: ActiveFilter) => void
  onItemCountChange: (count?: number) => void
  onSearchChange: (search: string) => void
  onSearchToggle: (displayed: boolean) => void
  onTaskClick: (task: Task, checked: boolean) => void
  path: string[]
  projectId: number
  search?: string
  searchError?: string
  selectedTaskIds: Set<number>
  shown: boolean
}

export const TaskColumn: FC<Props> = ({
  children,
  disabledTaskIds,
  displaySearch,
  filter,
  heading,
  integrationId,
  onActiveFilterChange,
  onItemCountChange,
  onSearchChange,
  onSearchToggle,
  onTaskClick,
  path,
  projectId,
  search = '',
  searchError,
  selectedTaskIds,
  shown,
}) => {
  const debounced = useDebounce(search)

  const searchResult = useSearchTasks({
    integrationId,
    projectId,
    q: debounced,
    path,
    onCountChange: onItemCountChange,
    active: getActiveFromFilter(filter),
  })

  if (!shown) return null

  const { items, totalItems } = getItemsAndTotalCount(searchResult)

  return (
    <Column
      displaySearch={displaySearch}
      filter={filter}
      heading={heading}
      isFetching={searchResult.isFetchingNextPage || searchResult.isLoading}
      isInitiallyLoading={searchResult.isLoading}
      items={items}
      loadMoreItems={() => void searchResult.fetchNextPage()}
      activeFilterEnabled={true}
      onActiveFilterChange={onActiveFilterChange}
      onSearchChange={onSearchChange}
      onSearchToggle={onSearchToggle}
      renderItem={(task) => {
        return (
          <Item
            className="mx-9"
            key={`${task.id ?? ''}-${task.name}`}
            item={itemFromtask(task, disabledTaskIds, selectedTaskIds, {
              hideActive:
                // Hide the active diamond if we're filtering on either active or inactive,
                // display it when displaying both (so either active and inactive are both false or both true)
                (filter?.active ?? false) !== (filter?.inactive ?? false),
            })}
            onItemClick={(_id, checked) => onTaskClick(task, checked)}
          />
        )
      }}
      searchError={searchError}
      searchValue={search}
      skeletonItem={<SkeletonItem />}
      totalItems={totalItems}
    >
      {children}
    </Column>
  )
}
